import { Component, Input, ViewChild, Inject, Output, EventEmitter, ChangeDetectorRef, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent } from "../../shared";
import * as Immutable from "immutable";
import { RefObject, GlobalSettingsCommand, GlobalDataRequest, ApiResponse, GlobalSettings } from "../../shared/models";
import { MessageBoxConfig, MessageBoxComponent } from "../../../shared/components/messageBox";
import { ModelFactory } from "../../shared/providers/modelFactory";
import { GlobalDataStore } from "../../shared/providers/globalData/globalDataStore";
import { HttpService } from "../../../shared/providers";

@Component({
  selector: "scheduled-changes-list",
  templateUrl: "./scheduledChangesListComponent.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScheduledChangesListComponent extends BaseComponent {
  @Input()
  public type: string;

  @Output()
  onRemoveScheduledChanges = new EventEmitter<any>();

  listItems: Immutable.List<ScheduledChangesListItem> = Immutable.List<ScheduledChangesListItem>();

  @ViewChild(MessageBoxComponent)
  public confirmScheduleChangesMessageBox: MessageBoxComponent;

  constructor(
    @Inject(ModelFactory) public modelFactory: ModelFactory,
    @Inject(GlobalDataStore) public globalDataStore: GlobalDataStore,
    protected httpService: HttpService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    let data: Immutable.List<RefObject>;
    if (this.type == "bom")
      data = this.globalDataStore.globalSettings.scheduledBomChanges;
    else if (this.type == "price")
      data = this.globalDataStore.globalSettings.scheduledPriceChanges;

    if (data) {
      let convertedData = this.convertRefObjectsToListItems(data);
      if (convertedData)
        this.addListItems(convertedData);
    }
  }

  onConfirmRemoveScheduledChangeClick($event) {
    let info: MessageBoxConfig<any> = new MessageBoxConfig<any>();
    info.icon = "warning";
    info.headerText = `Are you sure you want to delete the scheduled change for ${$event.product}?`;
    info.tag = { id: $event.id, product: $event.product, type: this.type };
    this.confirmScheduleChangesMessageBox.show(info);
  }

  onPerformRemoveScheduledChangeClick($event) {
    this.onRemoveScheduledChanges.emit($event);
  }

  public removeListItem(idToRemove: string) {
    if (!idToRemove)
      return;

    let itemIndexToRemove = this.listItems.findIndex(i => i.id == idToRemove);
    if (itemIndexToRemove == null)
      return;

    this.listItems = this.listItems.remove(itemIndexToRemove);

    this.cdr.markForCheck();
  }

  convertRefObjectsToListItems(itemsToConvert: Immutable.List<RefObject>) {
    let convertedItems: Immutable.List<ScheduledChangesListItem> = Immutable.List<ScheduledChangesListItem>();

    itemsToConvert.forEach(itemToConvert => {
      let values: string[] = itemToConvert.text.split("#");
      // The initial structure in the array is [Product, PriceListCateCory, Date, User]
      let scheduledChange: ScheduledChangesListItem = {
        product: values.shift(), // Product is always the first item, get it and remove it from the list. Structure after the item has been removed. [PriceListCategory, Date, User]
        user: values.pop(), // User is the last, get the value and remove it for the array. Structure after the item has been removed. [PriceListCategory, Date]
        date: values.pop(), // The date is now the last one, get it and remove it from the list. Structure after the item has been removed. [PriceListCategory]
        id: itemToConvert.longId.toString()
      };

      if (values.length == 1)
        scheduledChange.priceListCategory = values.pop(); // The only one left is PriceListCategory.

      convertedItems = convertedItems.push(scheduledChange);
    });

    return convertedItems;
  }

  public addListItems(scheduledChangeData: Immutable.List<ScheduledChangesListItem>) {
    scheduledChangeData.forEach(scheduledChange => this.addListItem(scheduledChange));
  }

  public addListItem(scheduledChangeData: ScheduledChangesListItem, sort: boolean = false) {
    if (!scheduledChangeData)
      return;

    this.listItems = this.listItems.push(scheduledChangeData);

    if (sort)
      this.listItems = this.listItems.sortBy(o => o.date).toList();

    this.cdr.markForCheck();
  }
}

export interface ScheduledChangesListItem {
  product: string;
  priceListCategory?: string;
  date: string;
  id: string;
  user: string;
}